import { useState } from 'react'
import { Download, Trash2, X } from 'lucide-react'
import type { RequirementsSummary, RequirementStatus, RequirementPriorityLevel } from '@ispot/shared'
import { REQUIREMENT_STATUS_LABELS, REQUIREMENT_PRIORITY_LABELS } from './constants'
import { STATUS_TOKENS } from './cells'

interface RequirementsStatusBarProps {
  summary: RequirementsSummary | null
  selectedCount: number
  onDeselectAll: () => void
  onBulkStatus: (status: RequirementStatus) => void
  onBulkPriority: (priority: RequirementPriorityLevel | null) => void
  onBulkDelete: () => void
}

const STATUS_ORDER: RequirementStatus[] = ['done', 'in-progress', 'in-review', 'backlog']

export function RequirementsStatusBar({
  summary, selectedCount, onDeselectAll,
  onBulkStatus, onBulkPriority, onBulkDelete,
}: RequirementsStatusBarProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Bulk action bar
  if (selectedCount > 0) {
    return (
      <div
        className="flex items-center justify-between gap-3 flex-shrink-0 px-4 py-2"
        style={{
          borderTop: '1px solid var(--color-warm-200)',
          background: 'color-mix(in oklab, var(--color-warm-50) 80%, transparent)',
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--color-fg)' }}
          >
            {selectedCount} selected
          </span>

          <span style={{ width: 1, height: 16, background: 'var(--color-warm-200)' }} aria-hidden />

          <select
            className="text-sm cursor-pointer bg-transparent border-0 outline-none px-1.5 py-1 rounded-md"
            style={{
              color: 'var(--color-fg-secondary)',
              fontFamily: 'var(--font-body)',
            }}
            value=""
            onChange={e => { if (e.target.value) onBulkStatus(e.target.value as RequirementStatus) }}
          >
            <option value="">Set status…</option>
            {Object.entries(REQUIREMENT_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          <select
            className="text-sm cursor-pointer bg-transparent border-0 outline-none px-1.5 py-1 rounded-md"
            style={{
              color: 'var(--color-fg-secondary)',
              fontFamily: 'var(--font-body)',
            }}
            value=""
            onChange={e => { if (e.target.value) onBulkPriority(e.target.value === 'none' ? null : e.target.value as RequirementPriorityLevel) }}
          >
            <option value="">Set priority…</option>
            {Object.entries(REQUIREMENT_PRIORITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
            <option value="none">Clear priority</option>
          </select>

          {confirmDelete ? (
            <span className="flex items-center gap-2">
              <span className="text-sm" style={{ color: 'var(--color-danger-fg)' }}>Delete {selectedCount} items?</span>
              <button
                className="text-sm font-medium cursor-pointer border-0 rounded-md px-2.5 py-1"
                style={{ background: 'var(--color-danger-fg)', color: 'white', fontFamily: 'var(--font-body)' }}
                onClick={() => { onBulkDelete(); setConfirmDelete(false) }}
              >
                Confirm
              </button>
              <button
                className="text-sm cursor-pointer bg-transparent border-0 rounded-md px-2.5 py-1"
                style={{ color: 'var(--color-fg-secondary)', fontFamily: 'var(--font-body)' }}
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              className="inline-flex items-center gap-1.5 text-sm cursor-pointer bg-transparent border-0 px-2 py-1 rounded-md transition-colors"
              style={{ color: 'var(--color-danger-fg)', fontFamily: 'var(--font-body)' }}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>

        <button
          className="inline-flex items-center gap-1.5 text-sm cursor-pointer bg-transparent border-0 px-2 py-1 rounded-md transition-colors"
          style={{ color: 'var(--color-fg-muted)', fontFamily: 'var(--font-body)' }}
          onClick={onDeselectAll}
        >
          <X className="h-4 w-4" /> Clear
        </button>
      </div>
    )
  }

  if (!summary) return null

  // Documents3-style flat status row: colored dots + counts, no progress bar widget.
  return (
    <div
      className="flex items-center justify-between gap-4 flex-shrink-0 px-4 py-2"
      style={{
        borderTop: '1px solid var(--color-warm-200)',
        background: 'color-mix(in oklab, var(--color-warm-50) 80%, transparent)',
      }}
    >
      <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--color-fg-secondary)' }}>
        {STATUS_ORDER.map(status => {
          const count = summary.byStatus[status] ?? 0
          const t = STATUS_TOKENS[status]
          return (
            <span
              key={status}
              className="inline-flex items-center gap-1.5"
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: t.color,
                }}
              />
              <span className="tabular-nums font-medium" style={{ color: 'var(--color-fg)' }}>
                {count}
              </span>
              {t.label}
            </span>
          )
        })}
      </div>

      <button
        className="inline-flex items-center gap-1.5 text-sm cursor-pointer bg-transparent border-0 px-2 py-1 rounded-md transition-colors"
        style={{ color: 'var(--color-fg-muted)', fontFamily: 'var(--font-body)' }}
      >
        <Download className="h-4 w-4" />
        Export
      </button>
    </div>
  )
}
